import { Checkbox, cn } from "@devhop/ui";
import type { ShopCatalogSkuListingItem } from "../api/client";

const SHOP_STATUS_LABELS: Record<string, string> = {
  active: "ເປີດໃຊ້",
  suspended: "ຖືກລະງັບ",
  unverified: "ລໍອະນຸມັດ",
};

const REASON_LABELS: Record<string, string> = {
  FITMENT_PENDING: "fitment ຍັງບໍ່ຄົບ",
  NO_STOCK: "ບໍ່ມີສະຕ໋ອກ",
  SKU_INACTIVE: "SKU ປິດໃຊ້",
  LISTING_REMOVED: "listing ຖືກລຶບ",
  SHOP_MISMATCH: "ຮ້ານບໍ່ກົງ",
  SKU_NOT_FOUND: "ບໍ່ມີ SKU ໃນຮ້ານ",
};

type ListingShopCheckboxPickerProps = {
  shops: ShopCatalogSkuListingItem[];
  checkedShopIds: string[];
  onCheckedChange: (shopIds: string[]) => void;
  isLoading?: boolean;
};

function ShopCheckboxRow({
  shop,
  checked,
  onToggle,
}: {
  shop: ShopCatalogSkuListingItem;
  checked: boolean;
  onToggle: () => void;
}) {
  const noSku = !shop.shopProductSkuId;
  const disabled = noSku || shop.shopStatus !== "active";

  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors",
        checked && "border-primary/40 bg-primary/5",
        !checked && "bg-muted/20",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => {
          if (!disabled) onToggle();
        }}
        className="mt-0.5"
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm">{shop.shopName}</span>
          {shop.hasListing ? (
            <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-emerald-900 text-[10px]">
              ເຂົ້າຮ່ວມແລ້ວ
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 block text-muted-foreground text-xs">
          {SHOP_STATUS_LABELS[shop.shopStatus] ?? shop.shopStatus}
          {shop.city ? ` · ${shop.city}` : ""}
          {shop.shopSku ? ` · ${shop.shopSku}` : ""}
          {shop.shopProductSkuId && shop.availableQty >= 0
            ? ` · ຄົງ ${shop.availableQty}`
            : ""}
        </span>
        {noSku ? (
          <span className="mt-0.5 block text-amber-700 text-xs">
            ຮ້ານຍັງບໍ່ມີ SKU ນີ້
          </span>
        ) : !shop.hasListing && !shop.eligible && shop.eligibilityReasons.length > 0 ? (
          <span className="mt-0.5 block text-destructive text-xs">
            {shop.eligibilityReasons
              .map((r) => REASON_LABELS[r] ?? r)
              .join(" · ")}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export function ListingShopCheckboxPicker({
  shops,
  checkedShopIds,
  onCheckedChange,
  isLoading,
}: ListingShopCheckboxPickerProps) {
  const toggle = (shopId: string) => {
    onCheckedChange(
      checkedShopIds.includes(shopId)
        ? checkedShopIds.filter((id) => id !== shopId)
        : [...checkedShopIds, shopId],
    );
  };

  const selectable = shops.filter(
    (s) => s.shopProductSkuId && s.shopStatus === "active",
  );

  const selectAllNew = () => {
    const ids = selectable
      .filter((s) => !s.hasListing && s.eligible)
      .map((s) => s.shopId);
    const merged = new Set([...checkedShopIds, ...ids]);
    onCheckedChange([...merged]);
  };

  if (isLoading) {
    return (
      <p className="text-muted-foreground py-4 text-center text-sm">
        ກຳລັງໂຫຼດຮ້ານ...
      </p>
    );
  }

  if (shops.length === 0) {
    return (
      <p className="text-muted-foreground py-4 text-center text-sm">
        ບໍ່ພົບຮ້ານ
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="text-primary text-xs underline underline-offset-2"
          onClick={() =>
            onCheckedChange(
              shops.filter((s) => s.hasListing).map((s) => s.shopId),
            )
          }
        >
          ເລືອກຮ້ານທີ່ເຂົ້າຮ່ວມແລ້ວ
        </button>
        <button
          type="button"
          className="text-primary text-xs underline underline-offset-2"
          onClick={selectAllNew}
        >
          ເລືອກຮ້ານໃໝ່ທີ່ພ້ອມ publish
        </button>
        <button
          type="button"
          className="text-muted-foreground text-xs underline underline-offset-2"
          onClick={() => onCheckedChange([])}
        >
          ລ້າງ
        </button>
      </div>
      <div className="max-h-80 space-y-1.5 overflow-y-auto pr-0.5">
        {shops.map((shop) => (
          <ShopCheckboxRow
            key={shop.shopId}
            shop={shop}
            checked={checkedShopIds.includes(shop.shopId)}
            onToggle={() => toggle(shop.shopId)}
          />
        ))}
      </div>
    </div>
  );
}
